import { useState } from "react";
import { toast } from "sonner";
import { Download, PenLine, Link2, Mail, Copy, Lock, Loader2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { SignaturePad } from "@/components/SignaturePad";
import { Field, Input, Btn, Card, Badge, download } from "./ui";

const H = ({ children }) => <h3 className="mb-3 font-display text-xl font-bold uppercase">{children}</h3>;
const STATUS = { venter_signatur: ["Venter på signatur", "amber"], delvis_signert: ["Delvis signert", "sky"], signert: ["Signert og låst", "green"], ferdig: ["Ferdig", "zinc"] };
const fmt = (s) => (s ? new Date(s).toLocaleString("nb-NO") : "");

const SellerSign = ({ doc, onDone, onCancel }) => {
  const [name, setName] = useState("");
  const [sig, setSig] = useState(null);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/admin/vehicle-docs/${doc.id}/sign-seller`, { name, image: sig, accepted: ok }); toast.success("Signert som selger"); onDone(); }
    catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 space-y-3 border border-white/10 p-3" data-testid={`seller-sign-${doc.id}`}>
      <Field label="Navn på den som signerer for selger"><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="seller-sign-name" /></Field>
      <SignaturePad onChange={setSig} testid="seller-pad" dark />
      <label className="flex items-start gap-2 text-sm text-zinc-200"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-1" data-testid="seller-sign-accept" /> Jeg har kontrollert dokumentet og signerer på vegne av selger.</label>
      <div className="flex gap-2">
        <Btn variant="red" onClick={save} disabled={!sig || !ok || name.trim().length < 2 || busy} data-testid="seller-sign-save">{busy && <Loader2 size={14} className="animate-spin" />} Lagre signatur</Btn>
        <Btn onClick={onCancel}>Avbryt</Btn>
      </div>
    </div>
  );
};

export const BuyerLink = ({ doc, buyerEmail, onDone }) => {
  const [link, setLink] = useState(null);
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const make = async (send) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/vehicle-docs/${doc.id}/sign-link`, { send_email: send });
      setLink(data);
      setAsk(false);
      toast.success(send ? `Signeringslenke sendt til ${data.emailed_to}` : "Ny signeringslenke laget (gamle lenker er ugyldige)");
      onDone();
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(link.url); toast.success("Lenke kopiert"); } catch { toast.error("Kunne ikke kopiere – merk og kopier manuelt"); } };
  return (
    <div className="mt-3 space-y-2 border border-white/10 p-3" data-testid={`buyer-link-${doc.id}`}>
      <p className="text-xs text-zinc-400">Personlig engangslenke til kjøper, gyldig i 7 dager. En ny lenke gjør tidligere lenker ugyldige.</p>
      <div className="flex flex-wrap gap-2">
        <Btn onClick={() => make(false)} disabled={busy} data-testid="buyer-link-create"><Link2 size={14} /> Lag lenke</Btn>
        <Btn onClick={() => setAsk(true)} disabled={busy || !buyerEmail} data-testid="buyer-link-email"><Mail size={14} /> Send på e-post</Btn>
      </div>
      {!buyerEmail && <p className="text-xs text-amber-300">Kjøper mangler e-post i dokumentet – lag lenke og send den via SMS/WhatsApp, eller lag nytt dokument med e-post.</p>}
      {ask && (
        <div className="flex flex-wrap items-center gap-2 border border-amber-500/40 p-2" data-testid="buyer-link-confirm">
          <span className="text-sm text-zinc-200">Sende signeringslenke til <b>{buyerEmail}</b>?</span>
          <Btn variant="red" onClick={() => make(true)} disabled={busy} data-testid="buyer-link-confirm-yes">Ja, send</Btn>
          <Btn onClick={() => setAsk(false)}>Avbryt</Btn>
        </div>
      )}
      {link && (
        <div className="flex items-center gap-2">
          <Input readOnly value={link.url} onFocus={(e) => e.target.select()} data-testid="buyer-link-url" />
          <Btn onClick={copy} data-testid="buyer-link-copy"><Copy size={14} /></Btn>
        </div>
      )}
    </div>
  );
};

const DocRow = ({ x, reload }) => {
  const [mode, setMode] = useState("");
  const [label, tone] = STATUS[x.status] ?? ["Ferdig", "zinc"];
  const sigs = x.signatures ?? {};
  const open = x.signable && x.status !== "signert";
  return (
    <div className="py-3" data-testid={`sale-doc-${x.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm">
          <span className="font-mono font-bold">{x.number}</span> · {x.title} {x.version > 1 && <span className="text-zinc-400">(versjon {x.version})</span>}
          <span className="text-zinc-400"> · {fmt(x.created_at)}</span>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {x.signable ? <Badge tone={tone}>{x.status === "signert" && <Lock size={10} className="mr-1 inline" />}{label}</Badge> : <Badge>Signeres ikke</Badge>}
            {sigs.seller && <span className="text-xs text-zinc-400">Selger: {sigs.seller.name}</span>}
            {sigs.buyer && <span className="text-xs text-zinc-400">Kjøper: {sigs.buyer.name}</span>}
          </div>
          {Array.isArray(x.auto_email) && x.auto_email.length > 0 && (
            <div className="mt-1 text-xs text-zinc-400" data-testid={`sale-doc-auto-email-${x.id}`}>
              Signert kopi: {x.auto_email.map((a) => `${a.to || "kjøper"} ${a.ok ? "sendt" : `ikke sendt (${a.error})`}`).join(" · ")}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {open && !sigs.seller && <Btn onClick={() => setMode(mode === "sign" ? "" : "sign")} data-testid={`sale-doc-sign-${x.id}`}><PenLine size={14} /> Signer (selger)</Btn>}
          {open && !sigs.buyer && <Btn onClick={() => setMode(mode === "link" ? "" : "link")} data-testid={`sale-doc-link-${x.id}`}><Link2 size={14} /> Kjøper signerer</Btn>}
          <Btn onClick={() => download(`/admin/vehicle-docs/${x.id}/download`, x.filename)} data-testid={`sale-doc-download-${x.id}`}><Download size={14} /> {x.status === "signert" ? "Signert PDF" : "Last ned"}</Btn>
        </div>
      </div>
      {mode === "sign" && <SellerSign doc={x} onCancel={() => setMode("")} onDone={() => { setMode(""); reload(); }} />}
      {mode === "link" && <BuyerLink doc={x} buyerEmail={x.buyer_email} onDone={reload} />}
    </div>
  );
};

export const DocHistory = ({ docs, reload }) => (
  <Card data-testid="sale-doc-history">
    <H>Dokumenthistorikk og signering</H>
    <p className="mb-2 text-xs text-zinc-400">Når både selger og kjøper har signert, låses dokumentet. Trenger du endringer, lag en ny versjon over.</p>
    {docs.length === 0 ? <p className="text-sm text-zinc-400">Ingen dokumenter laget ennå.</p> : <div className="divide-y divide-white/10">{docs.map((x) => <DocRow key={x.id} x={x} reload={reload} />)}</div>}
  </Card>
);

export const EmailDocs = ({ vehicleId, docs, buyerEmail, log, reload }) => {
  const [sel, setSel] = useState([]);
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/vehicles/${vehicleId}/email-docs`, { doc_ids: sel }); toast.success(`${data.count} dokument(er) sendt til ${data.sent_to}`); setAsk(false); setSel([]); reload(); }
    catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <Card data-testid="email-docs">
      <H>Send dokumenter på e-post</H>
      <p className="mb-3 text-xs text-zinc-400">Kjøper får sikre nedlastingslenker (gyldige i 30 dager). Svar går til info@rbtun.no. Mottaker: <b className="text-zinc-200">{buyerEmail || "mangler – fyll inn e-post under Kjøper og lagre utkast"}</b></p>
      <div className="space-y-1">
        {docs.map((x) => (
          <label key={x.id} className="flex items-center gap-2 text-sm text-zinc-200">
            <input type="checkbox" checked={sel.includes(x.id)} onChange={() => toggle(x.id)} data-testid={`email-doc-check-${x.id}`} />
            {x.number} · {x.title} {x.status === "signert" ? "(signert)" : x.signable ? "(ikke ferdig signert)" : ""}
          </label>
        ))}
        {docs.length === 0 && <p className="text-sm text-zinc-400">Ingen dokumenter å sende.</p>}
      </div>
      {!ask ? (
        <Btn className="mt-3" onClick={() => setAsk(true)} disabled={!sel.length || !buyerEmail} data-testid="email-docs-send"><Mail size={14} /> Send valgte</Btn>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-2 border border-amber-500/40 p-2" data-testid="email-docs-confirm">
          <span className="text-sm text-zinc-200">Sende {sel.length} dokument(er) til <b>{buyerEmail}</b>?</span>
          <Btn variant="red" onClick={send} disabled={busy} data-testid="email-docs-confirm-yes">{busy && <Loader2 size={14} className="animate-spin" />} Ja, send</Btn>
          <Btn onClick={() => setAsk(false)}>Avbryt</Btn>
        </div>
      )}
      {log?.length > 0 && (
        <div className="mt-4 border-t border-white/10 pt-3 text-xs text-zinc-400" data-testid="email-log">
          {log.map((l) => <div key={l.id}>{fmt(l.sent_at)} · {l.kind === "signering" ? "Signeringslenke" : l.kind === "signert_kopi" ? "Signert kontrakt (automatisk)" : `${l.doc_ids.length} dokument(er)`} → {l.to}</div>)}
        </div>
      )}
    </Card>
  );
};
